<template>
  <div class="min-h-screen font-sans text-gray-900 bg-[#0a0f18] relative">
    
    <canvas ref="canvasRef" class="fixed inset-0 z-0 w-full h-full pointer-events-none"></canvas>

    <div v-if="!isAdminRoute && !isAuthPage" class="max-w-md mx-auto min-h-screen shadow-2xl relative flex flex-col z-10 bg-white/20 backdrop-blur-lg border-x border-white/10 pb-16">
      
      <header class="bg-blue-800/90 backdrop-blur-md text-white p-4 shadow-md sticky top-0 z-20 flex justify-center items-center shrink-0 border-b border-white/20">
        <h1 class="text-lg font-bold">Global Meridian Cycle</h1>
      </header>

      <main class="flex-1 overflow-y-auto relative p-4">
        
        <div v-if="isGlobalLoading" class="absolute inset-0 z-50 flex items-center justify-center bg-white/40 backdrop-blur-md min-h-full">
          <div class="text-center flex flex-col items-center bg-white/70 p-6 rounded-2xl shadow-xl border border-white/50">
            <svg class="animate-spin h-10 w-10 text-blue-800 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-sm font-extrabold text-blue-900 tracking-wide">글로벌 메리디안 싸이클</p>
            <p class="text-xs font-bold text-gray-600 mt-2">보안 로딩중...</p>
          </div>
        </div>
        
        <router-view/>
      </main>

      <nav class="fixed bottom-0 w-full max-w-md bg-white/80 backdrop-blur-xl border-t border-white/30 flex justify-around p-3 z-10 shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <router-link to="/" class="text-gray-600 hover:text-blue-800 flex flex-col items-center">
          <span class="text-sm font-bold">회원 대시보드</span>
        </router-link>
        <router-link v-if="isAdmin" to="/admin" class="text-gray-600 hover:text-blue-800 flex flex-col items-center">
          <span class="text-sm font-bold text-red-600">관리자</span>
        </router-link>
      </nav>
    </div>

    <div v-else class="w-full min-h-screen relative z-10">
      <router-view/>
    </div>

  </div>
</template>

<script>
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export default {
  name: 'App',
  data() {
    return {
      isAdmin: false,
      isGlobalLoading: true,
      ctx: null,
      particles: [],
      particleCount: 60,
      animationFrameId: null
    };
  },
  computed: {
    isAuthPage() {
      return ['Login', 'Signup'].includes(this.$route.name);
    },
    isAdminRoute() {
      return this.$route.path.startsWith('/admin');
    }
  },
  mounted() {
    this.initCanvas();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeCanvas);
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
  },
  created() {
    this.$router.beforeEach((to, from, next) => {
      this.isGlobalLoading = true;
      next();
    });

    this.$router.afterEach(() => {
      setTimeout(() => {
        this.isGlobalLoading = false;
      }, 400);
    });

    onAuthStateChanged(auth, async (user) => {
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().role === 'admin') {
          this.isAdmin = true;
        } else {
          this.isAdmin = false;
        }
      } else {
        this.isAdmin = false;
      }
      setTimeout(() => {
        this.isGlobalLoading = false;
      }, 400);
    });
  },
  methods: {
    initCanvas() {
      if (!this.$refs.canvasRef) return;
      this.ctx = this.$refs.canvasRef.getContext('2d');
      this.resizeCanvas();
      window.addEventListener('resize', this.resizeCanvas);
      for (let i = 0; i < this.particleCount; i++) {
        this.particles.push({
          x: Math.random() * this.$refs.canvasRef.width,
          y: Math.random() * this.$refs.canvasRef.height,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          radius: Math.random() * 1.2 + 0.5
        });
      }
      this.animate();
    },
    resizeCanvas() {
      if (!this.$refs.canvasRef) return;
      this.$refs.canvasRef.width = window.innerWidth;
      this.$refs.canvasRef.height = window.innerHeight;
    },
    animate() {
      if (!this.$refs.canvasRef || !this.ctx) return;
      this.ctx.clearRect(0, 0, this.$refs.canvasRef.width, this.$refs.canvasRef.height);
      for (let i = 0; i < this.particles.length; i++) {
        let p = this.particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > this.$refs.canvasRef.width) p.vx *= -1;
        if (p.y < 0 || p.y > this.$refs.canvasRef.height) p.vy *= -1;
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        this.ctx.fillStyle = 'rgba(255, 204, 0, 0.4)';
        this.ctx.fill();
        for (let j = i + 1; j < this.particles.length; j++) {
          let p2 = this.particles[j];
          let dx = p.x - p2.x;
          let dy = p.y - p2.y;
          let dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            this.ctx.beginPath();
            this.ctx.moveTo(p.x, p.y);
            this.ctx.lineTo(p2.x, p2.y);
            this.ctx.strokeStyle = `rgba(255, 204, 0, ${0.8 - dist / 100})`;
            this.ctx.lineWidth = 0.5;
            this.ctx.stroke();
          }
        }
      }
      this.animationFrameId = requestAnimationFrame(this.animate);
    }
  }
}
</script>